import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle } from 'lucide-react';

interface UnsavedChangesRegistry {
  [key: string]: () => boolean; // page identifier -> hasUnsavedChanges function
}

interface NavigationContextType {
  interceptedNavigate: NavigateFunction;
  registerUnsavedChanges: (pageId: string, hasUnsavedChanges: () => boolean) => void;
  unregisterUnsavedChanges: (pageId: string) => void;
  showUnsavedDialog: boolean;
  setShowUnsavedDialog: (show: boolean) => void;
  pendingNavigation: (() => void) | null;
  setPendingNavigation: (nav: (() => void) | null) => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const NavigationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [unsavedChangesRegistry, setUnsavedChangesRegistry] = useState<UnsavedChangesRegistry>({});
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const navigateRef = useRef(navigate);

  // Update ref when navigate changes
  React.useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  // Register a page's unsaved changes checker
  const registerUnsavedChanges = useCallback((pageId: string, hasUnsavedChanges: () => boolean) => {
    setUnsavedChangesRegistry(prev => ({
      ...prev,
      [pageId]: hasUnsavedChanges,
    }));
  }, []);

  // Unregister a page's unsaved changes checker
  const unregisterUnsavedChanges = useCallback((pageId: string) => {
    setUnsavedChangesRegistry(prev => {
      const next = { ...prev };
      delete next[pageId];
      return next;
    });
  }, []);

  // Check if any registered page has unsaved changes
  const hasAnyUnsavedChanges = useCallback(() => {
    return Object.values(unsavedChangesRegistry).some(checker => checker());
  }, [unsavedChangesRegistry]);

  // Intercepted navigate function that checks for unsaved changes
  const interceptedNavigate = useCallback<NavigateFunction>(
    (to: string | number, options?: any) => {
      // Check if we have unsaved changes
      if (hasAnyUnsavedChanges()) {
        // Show custom dialog
        setShowUnsavedDialog(true);
        setPendingNavigation(() => () => {
          // Execute navigation after user confirms
          if (typeof to === 'number') {
            navigateRef.current(to);
          } else {
            navigateRef.current(to, options);
          }
        });
        return;
      }

      // No unsaved changes, proceed with navigation
      if (typeof to === 'number') {
        navigateRef.current(to);
      } else {
        navigateRef.current(to, options);
      }
    },
    [hasAnyUnsavedChanges, unsavedChangesRegistry]
  );

  return (
    <NavigationContext.Provider
      value={{
        interceptedNavigate,
        registerUnsavedChanges,
        unregisterUnsavedChanges,
        showUnsavedDialog,
        setShowUnsavedDialog,
        pendingNavigation,
        setPendingNavigation,
      }}
    >
      {children}
      
      {/* Unsaved Changes Dialog - Rendered at provider level so it persists */}
      <AlertDialog open={showUnsavedDialog} onOpenChange={(open) => {
        if (!open) {
          setShowUnsavedDialog(false);
          setPendingNavigation(null);
        }
      }}>
        <AlertDialogContent className="max-w-md p-0 overflow-hidden rounded-xl border border-gray-200 shadow-2xl">
          {/* Top accent strip */}
          <div className="h-1 w-full bg-gradient-to-r from-amber-400 via-orange-400 to-red-400" />

          <div className="p-6">
            <AlertDialogHeader className="space-y-3 text-left">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 ring-4 ring-amber-50">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <AlertDialogTitle className="text-base font-semibold text-gray-900 leading-tight">
                    You have unsaved changes
                  </AlertDialogTitle>
                  <AlertDialogDescription className="mt-1.5 text-sm text-gray-600 leading-relaxed">
                    Your edits haven't been saved yet. If you leave now, they'll be permanently lost.
                  </AlertDialogDescription>
                </div>
              </div>
            </AlertDialogHeader>

            <AlertDialogFooter className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <AlertDialogCancel
                onClick={() => {
                  setShowUnsavedDialog(false);
                  setPendingNavigation(null);
                }}
                className="mt-0 h-9 rounded-md border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 hover:text-gray-900 focus-visible:ring-2 focus-visible:ring-gray-200 focus-visible:ring-offset-0"
              >
                Keep editing
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  setShowUnsavedDialog(false);
                  if (pendingNavigation) {
                    pendingNavigation();
                    setPendingNavigation(null);
                  }
                }}
                className="h-9 rounded-md bg-red-600 px-4 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus-visible:ring-2 focus-visible:ring-red-200 focus-visible:ring-offset-0"
              >
                Discard changes
              </AlertDialogAction>
            </AlertDialogFooter>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

